
import React, { useState } from 'react';
import Button from './Button';
import PrinterIcon from './icons/PrinterIcon';
import CopyLinkIcon from './icons/CopyLinkIcon';
import { startSolutionGeneration } from '../services/apiService';
import { toast } from 'react-hot-toast';
import ShareButtons from './SharingButtons';
import { useTranslation } from 'react-i18next';

interface ShareSectionProps {
  solutionId: string; // Always requires an ID for sharing
  className?: string;
}

const ShareSection: React.FC<ShareSectionProps> = ({ solutionId, className = '' }) => {
  const { t } = useTranslation();
  const [copyStatus, setCopyStatus] = useState<string>('');
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);

  const getGeneratedShareLink = async (): Promise<string | null> => {
    if (isGeneratingLink) return null;
    setIsGeneratingLink(true);
    try {
      const data = await startSolutionGeneration(solutionId);

      return data.solution_page_url;
    } catch (error) {
      toast.error(t('shareSection.generateLinkError'));
      console.error("Failed to generate shareable link:", error);
      return null;
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const handleCopyToClipboard = async () => {
    setCopyStatus(t('shareSection.copying'));
    const link = await getGeneratedShareLink();
    if (link) {
      try {
        await navigator.clipboard.writeText(link);
        setCopyStatus(t('shareSection.linkCopied'));
      } catch (error) {
        setCopyStatus(t('shareSection.copyError'));
        toast.error(t('shareSection.copyToClipboardError'));
        console.error('Failed to copy link to clipboard:', error);
      }
    }
  };

  const handlePrint = () => {
    // If printing from history view, ensure only the expanded item is targeted
    const historyItemPrintableArea = solutionId ? document.getElementById(`printable-history-item-${solutionId}`) : null;
    const resultsViewPrintableArea = document.getElementById('printable-solution-content');

    if (historyItemPrintableArea) {
        // Temporarily hide everything else for printing only the specific history item
        const allBodyChildren = Array.from(document.body.children);
        allBodyChildren.forEach(child => {
            if (child !== historyItemPrintableArea && !historyItemPrintableArea.contains(child)) {
                (child as HTMLElement).classList.add('print-hide-others');
            }
        });
        historyItemPrintableArea.classList.add('print-only-this');
        window.print();
        historyItemPrintableArea.classList.remove('print-only-this');
        allBodyChildren.forEach(child => {
            (child as HTMLElement).classList.remove('print-hide-others');
        });

    } else if (resultsViewPrintableArea) {
        // Print logic for ResultsView
        const elementsToHideQuery = 'header, footer, .print-hidden-explicitly'; // Add classes to elements you want to hide in ResultsView specifically
        document.querySelectorAll(elementsToHideQuery).forEach(el => el.classList.add('print:hidden'));
        window.print();
         // No need to remove print:hidden as it's a utility class that only applies on print
    } else {
        // Fallback or for SharedSolutionView (which has its own print setup)
        window.print();
    }
  };

  return (
    <div className={`mt-6 pt-6 border-t border-slate-200 print:hidden ${className}`}>
      <h3 className="text-md font-semibold text-slate-700 mb-3 text-center">{t('shareSection.shareSolution')}</h3>
      <div className="flex flex-wrap justify-center gap-2 md:gap-3">
        <ShareButtons solutionId={solutionId} />
        <Button
          onClick={handleCopyToClipboard}
          disabled={isGeneratingLink || !!copyStatus}
          variant="secondary"
          size="sm"
          title={t('shareSection.copyLinkTitle')}
        >
          <CopyLinkIcon className="w-4 h-4 mr-2" /> {copyStatus || t('shareSection.copyLink')}
        </Button>
        <Button onClick={handlePrint} variant="secondary" size="sm" title={t('shareSection.printSolutionTitle')}>
          <PrinterIcon className="w-4 h-4 mr-2" /> {t('shareSection.print')}
        </Button>
      </div>
    </div>
  );
};

export default ShareSection;

// Basic styles for print-hide-others and print-only-this, if needed for complex scenarios
// These can be added to index.html style block or a global CSS file
/*
@media print {
  .print-hide-others {
    display: none !important;
  }
  .print-only-this {
    display: block !important;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
  }
}
*/